import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { api, ApiError } from '../api/client';
import type { Me, Membership } from '../api/types';

export const ME_KEY = ['me'] as const;

/** The signed-in user, or null when nobody is signed in. */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<Me | null> => {
      try {
        return await api<Me>('/api/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * The choir the user is working in. Someone in several choirs picks one in the header; the choice
 * is kept in this browser. Without a choice (or a stale one), the first membership is used.
 */
const ACTIVE_CHOIR_KEY = 'corocanto.choirId';
const choirListeners = new Set<() => void>();
let activeChoirId: string | null = readActiveChoir();

function readActiveChoir(): string | null {
  try {
    return localStorage.getItem(ACTIVE_CHOIR_KEY);
  } catch {
    return null;
  }
}

export function setActiveChoir(choirId: string) {
  activeChoirId = choirId;
  try {
    localStorage.setItem(ACTIVE_CHOIR_KEY, choirId);
  } catch {
    // Private mode or storage blocked: the choice lasts for this page only.
  }
  choirListeners.forEach((listener) => listener());
}

function useActiveChoirId(): string | null {
  return useSyncExternalStore(
    (listener) => {
      choirListeners.add(listener);
      return () => choirListeners.delete(listener);
    },
    () => activeChoirId,
  );
}

export function activeMembership(me: Me | null | undefined, choirId: string | null): Membership | undefined {
  return me?.memberships.find((m) => m.choirId === choirId) ?? me?.memberships[0];
}

export function useMembership(): Membership | undefined {
  return activeMembership(useMe().data, useActiveChoirId());
}

/** During the administrator's "view as" nothing can be changed: change controls are off. */
export function useReadOnly(): boolean {
  return !!useMe().data?.viewAs;
}

export function isDirector(m: Membership | undefined): boolean {
  return !!m?.roles.includes('DIRECTOR');
}

/** The administrator's password was right: the code from the authenticator app comes next. */
export interface TwoStepRequired {
  twoStepRequired: true;
}

export function isTwoStep(result: Me | TwoStepRequired | undefined): result is TwoStepRequired {
  return !!result && 'twoStepRequired' in result;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      api<Me | TwoStepRequired>('/api/auth/login', { method: 'POST', json: credentials }),
    onSuccess: (result) => {
      if (isTwoStep(result)) return;
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, result);
    },
  });
}

/** The administrator's second step: the 6-digit code. */
export function useLoginCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api<Me>('/api/auth/login/code', { method: 'POST', json: { code } }),
    onSuccess: (me) => {
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, me);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/api/auth/logout', { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, null);
    },
  });
}

/** Save the interface language to the user's account ("tr" or "en"). */
export function useUpdateLanguage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (locale: string) => api<Me>('/api/me', { method: 'PATCH', json: { locale } }),
    onSuccess: (me) => queryClient.setQueryData(ME_KEY, me),
  });
}
